/**
 * A route estimate for setting a generated cave's clock (docs/spec/03 §4): a greedy tour from the
 * inbox to the nearest diamond, then the next nearest, until the quota, then to the exit.
 */

import { E, isButterfly, isFirefly } from '../../sim/elements';
import type { BuiltCave } from '../types';

/** Dijkstra keys pack the distance above the cell index; caves have at most 80 x 50 cells. */
const PACK = 8192;

/** Rough scans to cross a cell: boulders are dug round, creatures waited out. 0: closed. */
function cellCost(el: number): number {
  switch (el) {
    case E.STEEL:
    case E.BRICK:
    case E.MAGIC_WALL:
    case E.EXPANDING_WALL:
    case E.SLIME:
    case E.OUTBOX_CLOSED:
      return 0;
    case E.BOULDER:
      return 4;
    case E.AMOEBA:
      return 8;
    default:
      if (el >= E.DOOR && el < E.DOOR + 3) return 3;
      return isFirefly(el) || isButterfly(el) ? 6 : 1;
  }
}

function push(heap: number[], v: number): void {
  let i = heap.length;
  heap.push(v);
  while (i > 0) {
    const p = (i - 1) >> 1;
    const pv = heap[p] ?? 0;
    if (pv <= v) break;
    heap[i] = pv;
    i = p;
  }
  heap[i] = v;
}

function pop(heap: number[]): number {
  const top = heap[0] ?? 0;
  const last = heap.pop() ?? 0;
  const n = heap.length;
  if (n === 0) return top;
  let i = 0;
  for (;;) {
    const l = 2 * i + 1;
    if (l >= n) break;
    const c = l + 1 < n && (heap[l + 1] ?? 0) < (heap[l] ?? 0) ? l + 1 : l;
    const cv = heap[c] ?? 0;
    if (cv >= last) break;
    heap[i] = cv;
    i = c;
  }
  heap[i] = last;
  return top;
}

/** Scratch arrays for one route: the settled distance and the best offer so far, per cell. */
interface Search {
  readonly cost: Uint8Array;
  readonly w: number;
  readonly h: number;
  readonly done: Uint8Array;
  readonly best: Int32Array;
}

/**
 * Dijkstra from `start` until the first cell marked in `target` is settled: that cell and its
 * cost, or undefined if none can be reached.
 */
function nearest(sr: Search, start: number, target: Uint8Array): { at: number; cost: number } | undefined {
  const { cost, w, h, done, best } = sr;
  done.fill(0);
  best.fill(0x7fffffff);
  best[start] = 0;
  const heap = [start];
  while (heap.length > 0) {
    const v = pop(heap);
    const i = v % PACK;
    const d = (v - i) / PACK;
    if (done[i] === 1) continue;
    done[i] = 1;
    if (target[i] === 1) return { at: i, cost: d };
    const x = i % w;
    const y = (i - x) / w;
    const next = [x > 0 ? i - 1 : -1, x < w - 1 ? i + 1 : -1, y > 0 ? i - w : -1, y < h - 1 ? i + w : -1];
    for (const n of next) {
      const c = n < 0 ? 0 : (cost[n] ?? 0);
      if (c === 0 || done[n] === 1) continue;
      const nd = d + c;
      if (nd < (best[n] ?? 0)) {
        best[n] = nd;
        push(heap, nd * PACK + n);
      }
    }
  }
  return undefined;
}

/**
 * The tour's length in rough scans: nearest diamond first until `quota`, then the exit. -1 if
 * the quota or the exit cannot be reached at all.
 */
export function routeLength(b: Pick<BuiltCave, 'w' | 'h' | 'cells'>, quota: number): number {
  const n = b.w * b.h;
  const sr: Search = { cost: new Uint8Array(n), w: b.w, h: b.h, done: new Uint8Array(n), best: new Int32Array(n) };
  const gems = new Uint8Array(n);
  const exits = new Uint8Array(n);
  b.cells.forEach((el, i) => {
    sr.cost[i] = cellCost(el);
    if (el === E.DIAMOND) gems[i] = 1;
    else if (el === E.OUTBOX_CLOSED) exits[i] = 1;
  });
  let pos = b.cells.indexOf(E.INBOX);
  if (pos < 0) return -1;
  sr.cost[pos] = 1;
  let total = 0;
  for (let k = 0; k < quota; k++) {
    const hit = nearest(sr, pos, gems);
    if (!hit) return -1;
    total += hit.cost;
    gems[hit.at] = 0;
    pos = hit.at;
  }
  // The exit opens once the quota is in: it can be stepped into now.
  exits.forEach((e, i) => {
    if (e === 1) sr.cost[i] = 1;
  });
  const out = nearest(sr, pos, exits);
  return out ? total + out.cost : -1;
}
